import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, Mail, Linkedin, Github, ArrowUpRight, Check } from "lucide-react";
import { profile } from "../data/mock";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received. I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send right now. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
            // 07 — Contact
          </p>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl font-bold tracking-tighter text-white text-balance">
            Let&rsquo;s build something
            <br />
            <span className="text-gradient-aurora animate-gradient-pan font-serif italic">
              that actually ships.
            </span>
          </h2>
          <p className="mt-5 max-w-xl text-cyan-100/70 text-base sm:text-lg leading-relaxed">
            Open to AI/ML Engineering, Generative AI, Applied ML, and MLOps roles.
            Drop a message — I read every one.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={onSubmit}
            className="lg:col-span-7 relative gradient-border rounded-2xl glass p-6 sm:p-8 space-y-5"
            data-testid="contact-form"
          >
            <div className="absolute -top-20 -left-20 h-48 w-48 bg-cyan-500/20 blur-3xl rounded-full pointer-events-none" />

            <div className="relative grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-cyan-300/80">
                  Name
                </label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your name"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-cyan-200/40 focus-visible:border-cyan-300 focus-visible:ring-cyan-300/20 h-11"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-cyan-300/80">
                  Email
                </label>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@company.com"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-cyan-200/40 focus-visible:border-cyan-300 focus-visible:ring-cyan-300/20 h-11"
                  data-testid="contact-input-email"
                />
              </div>
            </div>

            <div className="relative">
              <label className="font-mono text-[11px] uppercase tracking-widest text-cyan-300/80">
                Subject
              </label>
              <Input
                name="subject"
                value={form.subject}
                onChange={onChange}
                placeholder="What's this about?"
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-cyan-200/40 focus-visible:border-cyan-300 focus-visible:ring-cyan-300/20 h-11"
                data-testid="contact-input-subject"
              />
            </div>

            <div className="relative">
              <label className="font-mono text-[11px] uppercase tracking-widest text-cyan-300/80">
                Message
              </label>
              <Textarea
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="Tell me about the role, project, or idea…"
                rows={6}
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-cyan-200/40 focus-visible:border-cyan-300 focus-visible:ring-cyan-300/20"
                data-testid="contact-input-message"
              />
            </div>

            <div className="relative flex flex-wrap items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={loading || sent}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-white bg-gradient-to-r from-cyan-400 via-teal-400 to-sky-500 hover:from-cyan-300 hover:via-teal-300 hover:to-sky-400 disabled:opacity-60 disabled:cursor-not-allowed transition-[background] duration-300 glow-violet"
                data-testid="contact-submit-btn"
              >
                {sent ? (
                  <>
                    <Check className="h-4 w-4" />
                    Sent
                  </>
                ) : (
                  <>
                    {loading ? "Sending…" : "Send Message"}
                    <Send className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
              <p className="font-mono text-xs text-cyan-200/60">
                Typical response · within 24 hours
              </p>
            </div>
          </motion.form>

          {/* Direct contact */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative gradient-border rounded-2xl glass p-6 sm:p-8">
              <div className="absolute -top-16 -right-16 h-44 w-44 bg-cyan-400/15 blur-3xl rounded-full pointer-events-none" />

              <p className="relative font-mono text-[11px] uppercase tracking-widest text-rose-300">
                Or reach out directly
              </p>
              <ul className="relative mt-6 space-y-3">
                <li>
                  <a
                    href={`mailto:${profile.email}`}
                    className="group flex items-center justify-between gap-4 px-4 py-3 rounded-xl glass hover:bg-white/[0.07] transition-colors duration-200"
                    data-testid="contact-email-link"
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg glass-strong text-cyan-300 flex-shrink-0">
                        <Mail className="h-4 w-4" />
                      </span>
                      <span className="text-sm text-cyan-50 truncate">
                        {profile.email}
                      </span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-cyan-200/70 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-200 flex-shrink-0" />
                  </a>
                </li>
                <li>
                  <a
                    href={profile.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-4 px-4 py-3 rounded-xl glass hover:bg-white/[0.07] transition-colors duration-200"
                    data-testid="contact-linkedin-link"
                  >
                    <span className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg glass-strong text-cyan-300">
                        <Linkedin className="h-4 w-4" />
                      </span>
                      <span className="text-sm text-cyan-50">LinkedIn</span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-cyan-200/70 group-hover:text-white transition-colors duration-200" />
                  </a>
                </li>
                <li>
                  <a
                    href={profile.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-4 px-4 py-3 rounded-xl glass hover:bg-white/[0.07] transition-colors duration-200"
                    data-testid="contact-github-link"
                  >
                    <span className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg glass-strong text-pink-300">
                        <Github className="h-4 w-4" />
                      </span>
                      <span className="text-sm text-cyan-50">GitHub</span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-cyan-200/70 group-hover:text-white transition-colors duration-200" />
                  </a>
                </li>
              </ul>

              <div className="relative mt-8 pt-6 border-t border-white/5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/60">
                  Based in
                </p>
                <p className="mt-2 text-white">{profile.location}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
